import { scrollBarClassName } from '@/utils/scrollBarClassName';
import { ForwardedRef, forwardRef, TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '../ConditionalRenderer';
import Skeleton from '../Skeleton';
import { BaseInputProps } from './BaseInput';

export type weights =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900';

export type TextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
> &
  BaseInputProps & {
    color?: 'primary' | 'warning';
    typeInput?: string;
    centerText?: boolean;
    maxLenght?: number;
    onClickIcon?(): void;
    allowCopy?: boolean;
  };

interface IPreClasses {
  default: string;
  login: string;
}

export default forwardRef<HTMLTextAreaElement, TextAreaProps>(TextAreaInput);

function TextAreaInput(
  props: TextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    label,
    className,
    icon,
    testId,
    typeInput,
    color,
    errorLabelText,
    dataTestId,
    fontWeight,
    register,
    bgColor,
    fontSize,
    centerText,
    isLoading,
    labelPosition = 'top',
    errorLabelPosition = 'top',
    onClickIcon,
    isOptional,
    allowCopy,
    ...rest
  } = props;
  const name = rest.name || register?.name;
  const preClasses: IPreClasses = {
    default: `${
      color === 'warning'
        ? 'input-warning focus-visible:ring-error-content'
        : 'input-primary focus-visible:ring-primary-content'
    } input-sm pt-0.5`,
    login: 'h-10 focus:input-primary focus-visible:ring-primary-content',
  };

  const formatLabel = isOptional
    ? `${label} (${t('optional').toLowerCase()})`
    : label;

  return (
    <div
      className={`form-control relative flex ${twMerge('', className?.base)} ${
        labelPosition === 'left' ? 'flex-row w-full gap-3' : 'flex-col'
      }`}
    >
      <ConditionalRenderer condition={label}>
        <label
          htmlFor={name}
          className={twMerge('label p-0', className?.label)}
        >
          {formatLabel}
        </label>
      </ConditionalRenderer>
      <div className="flex-col w-full justify-center flex">
        <ConditionalRenderer condition={errorLabelText}>
          <label
            data-testid="errorLabel"
            className={`label text-error p-0 ${
              errorLabelPosition === 'bottom' ? 'absolute top-full w-full' : ''
            } ${fontSize || 'text-14'}`}
          >
            {errorLabelText}
          </label>
        </ConditionalRenderer>
        <div className="flex items-center relative w-full">
          <ConditionalRenderer
            condition={!isLoading}
            fallback={
              <Skeleton className="h-[70px] rounded-lg w-full bg-primary" />
            }
          >
            <textarea
              ref={ref}
              {...register}
              {...rest}
              id={name}
              data-testid={testId || dataTestId}
              className={twMerge(
                `leading-5 w-full focus-visible:ring-3 focus:outline-hidden min-h-[70px] font-500 placeholder:font-400 input input-bordered disabled:cursor-default disabled:bg-base-100 disabled:border-none input-sm pt-2`,
                fontWeight,
                typeInput
                  ? preClasses[typeInput as keyof IPreClasses]
                  : preClasses.default,
                color === 'warning'
                  ? 'input-warning focus-visible:ring-error-content'
                  : 'input-primary focus-visible:ring-primary-content',
                bgColor,
                fontSize,
                scrollBarClassName,
                centerText && 'text-center',
                className?.input,
                allowCopy && 'cursor-text select-text',
              )}
              style={props.style}
            />
          </ConditionalRenderer>
          <ConditionalRenderer condition={icon}>
            <label
              onClick={onClickIcon}
              className={`${onClickIcon ? 'cursor-pointer' : ''} ${
                errorLabelText ? 'text-error' : 'text-primary'
              } w-4 h-4 absolute right-2.5`}
            >
              {icon}
            </label>
          </ConditionalRenderer>
        </div>
      </div>
    </div>
  );
}
